import {
  Card,
  Col,
  DatePicker,
  Empty,
  Row,
  Space,
  Statistic,
  Table,
  Tag,
  Typography,
  theme as antdTheme,
} from 'antd';
import { useQuery } from '@tanstack/react-query';
import { useState } from 'react';
import dayjs, { type Dayjs } from 'dayjs';
import {
  Area,
  AreaChart,
  Bar,
  BarChart,
  CartesianGrid,
  Cell,
  Pie,
  PieChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import { reportsApi } from '@/api/reports';
import { PageHeader } from '@/shared/components/page-header';
import { ExportButton } from '@/shared/components/ui';
import { useSchoolTheme } from '@/themes/use-theme';

const PKR = (n: number | string) =>
  Number(n).toLocaleString('en-PK', { maximumFractionDigits: 2 });

const METHOD_LABEL: Record<string, string> = {
  CASH: 'Cash',
  BANK_TRANSFER: 'Bank transfer',
  ONLINE_TRANSFER: 'Online transfer',
};

export function FeeCollectionReportPage() {
  const { token } = antdTheme.useToken();
  const { chartColors } = useSchoolTheme();
  const [range, setRange] = useState<[Dayjs, Dayjs]>([dayjs().startOf('month'), dayjs().endOf('month')]);

  const from = range[0].format('YYYY-MM-DD');
  const to = range[1].format('YYYY-MM-DD');

  const { data, isFetching } = useQuery({
    queryKey: ['reports', 'fee-collection', from, to],
    queryFn: () => reportsApi.feeCollection(from, to),
  });

  const dailyChart = (data?.byDate ?? []).map((d) => ({
    date: dayjs(d.date).format('D MMM'),
    amount: Number(d.amount),
  }));

  const methodChart = (data?.byMethod ?? []).map((m) => ({
    name: METHOD_LABEL[m.method] ?? m.method,
    amount: Number(m.amount),
  }));

  const typeChart = (data?.byType ?? []).map((t) => ({
    name: t.feeTypeName ?? '—',
    amount: Number(t.amount),
  }));

  const tooltipStyle = {
    background: token.colorBgElevated,
    border: `1px solid ${token.colorBorderSecondary}`,
    borderRadius: 8,
  };

  return (
    <div style={{ display: 'grid', gap: 20 }}>
      <PageHeader
        eyebrow="Module / Reports"
        title="Fee collection summary"
        description="All receipts in the selected range, with breakdowns by fee type, day, payment method, collector and class. Reversed receipts are excluded from the breakdowns."
        actions={
          <ExportButton
            items={[{
              kind: 'xlsx',
              url: '/reports/exports/fee-collection.xlsx',
              params: { from, to },
              fileName: `fee-collection-${from}-to-${to}.xlsx`,
              disabled: !data,
            }]}
          />
        }
      />

      <Space wrap size={12}>
        <DatePicker.RangePicker
          value={range}
          onChange={(r) => r && r[0] && r[1] && setRange([r[0], r[1]])}
          format="DD MMM YYYY"
          allowClear={false}
        />
      </Space>

      {data ? (
        <>
          <Row gutter={[16, 16]}>
            <Col xs={12} md={6}>
              <Card>
                <Statistic title="Receipts" value={data.receiptCount} />
              </Card>
            </Col>
            <Col xs={12} md={6}>
              <Card>
                <Statistic title="Gross collected" value={PKR(data.grossAmount)} prefix="₨" />
              </Card>
            </Col>
            <Col xs={12} md={6}>
              <Card>
                <Statistic title="Reversed" value={PKR(data.reversedAmount)} prefix="₨" styles={{ content: { color: token.colorError } }} />
              </Card>
            </Col>
            <Col xs={12} md={6}>
              <Card>
                <Statistic title="Net" value={PKR(data.netAmount)} prefix="₨" styles={{ content: { color: token.colorSuccess } }} />
              </Card>
            </Col>
          </Row>

          {dailyChart.length > 0 && (
            <Card title={<Typography.Text strong>Daily collection trend</Typography.Text>}>
              <ResponsiveContainer width="100%" height={260}>
                <AreaChart data={dailyChart} margin={{ top: 8, right: 8, left: 8, bottom: 0 }}>
                  <defs>
                    <linearGradient id="feeArea" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor={chartColors[0]} stopOpacity={0.35} />
                      <stop offset="95%" stopColor={chartColors[0]} stopOpacity={0.02} />
                    </linearGradient>
                  </defs>
                  <CartesianGrid strokeDasharray="3 3" stroke={token.colorBorderSecondary} vertical={false} />
                  <XAxis dataKey="date" stroke={token.colorTextSecondary} fontSize={12} />
                  <YAxis stroke={token.colorTextSecondary} fontSize={12} tickFormatter={(v) => PKR(v as number)} width={80} />
                  <Tooltip formatter={(v: number) => [`₨ ${PKR(v)}`, 'Collected']} contentStyle={tooltipStyle} />
                  <Area type="monotone" dataKey="amount" stroke={chartColors[0]} strokeWidth={2} fill="url(#feeArea)" />
                </AreaChart>
              </ResponsiveContainer>
            </Card>
          )}

          <Row gutter={[16, 16]}>
            <Col xs={24} lg={14}>
              <Card title={<Typography.Text strong>Collection by fee type</Typography.Text>} style={{ height: '100%' }}>
                {typeChart.length === 0 ? (
                  <Empty description="No collections in this range" />
                ) : (
                  <ResponsiveContainer width="100%" height={Math.max(220, typeChart.length * 42)}>
                    <BarChart data={typeChart} layout="vertical" margin={{ left: 12, right: 24 }}>
                      <CartesianGrid strokeDasharray="3 3" stroke={token.colorBorderSecondary} horizontal={false} />
                      <XAxis type="number" stroke={token.colorTextSecondary} fontSize={12} tickFormatter={(v) => PKR(v as number)} />
                      <YAxis type="category" dataKey="name" stroke={token.colorTextSecondary} fontSize={12} width={120} />
                      <Tooltip formatter={(v: number) => [`₨ ${PKR(v)}`, 'Collected']} contentStyle={tooltipStyle} cursor={{ fill: token.colorFillTertiary }} />
                      <Bar dataKey="amount" fill={chartColors[1]} radius={[0, 4, 4, 0]} />
                    </BarChart>
                  </ResponsiveContainer>
                )}
              </Card>
            </Col>
            <Col xs={24} lg={10}>
              <Card title={<Typography.Text strong>By payment method</Typography.Text>} style={{ height: '100%' }}>
                {methodChart.length === 0 ? (
                  <Empty description="No data" />
                ) : (
                  <ResponsiveContainer width="100%" height={240}>
                    <PieChart>
                      <Pie
                        data={methodChart}
                        dataKey="amount"
                        nameKey="name"
                        innerRadius={55}
                        outerRadius={90}
                        paddingAngle={methodChart.length > 1 ? 2 : 0}
                        stroke="none"
                        startAngle={90}
                        endAngle={-270}
                      >
                        {methodChart.map((_, i) => (
                          <Cell key={i} fill={chartColors[i % chartColors.length]} />
                        ))}
                      </Pie>
                      <Tooltip formatter={(v: number, n) => [`₨ ${PKR(v)}`, n as string]} contentStyle={tooltipStyle} />
                    </PieChart>
                  </ResponsiveContainer>
                )}
                <Space orientation="vertical" size={4} style={{ width: '100%', marginTop: 8 }}>
                  {data.byMethod.map((m, i) => (
                    <Row key={m.method} justify="space-between">
                      <Col>
                        <span style={{ display: 'inline-block', width: 10, height: 10, borderRadius: 3, marginRight: 8, background: chartColors[i % chartColors.length] }} />
                        <Typography.Text>{METHOD_LABEL[m.method] ?? m.method}</Typography.Text>
                      </Col>
                      <Col><Typography.Text strong>₨ {PKR(m.amount)}</Typography.Text></Col>
                    </Row>
                  ))}
                </Space>
              </Card>
            </Col>
          </Row>

          <Row gutter={[16, 16]}>
            <Col xs={24} lg={12}>
              <Card title="By class · section" styles={{ body: { padding: 0 } }} style={{ height: '100%' }}>
                {data.byClassSection.length === 0 ? (
                  <Empty style={{ padding: 24 }} description="No data" />
                ) : (
                  <Table
                    rowKey={(r) => String(r.classSectionId)}
                    size="small"
                    pagination={false}
                    dataSource={data.byClassSection}
                    columns={[
                      { title: 'Class · Section', render: (_, r) => `${r.className} · ${r.sectionName}` },
                      { title: 'Receipts', dataIndex: 'receiptCount', width: 100, align: 'right' },
                      { title: 'Amount', dataIndex: 'amount', width: 150, align: 'right', render: (a: string) => <Typography.Text strong>₨ {PKR(a)}</Typography.Text> },
                    ]}
                  />
                )}
              </Card>
            </Col>
            <Col xs={24} lg={12}>
              <Card title="By collector" styles={{ body: { padding: 0 } }} style={{ height: '100%' }}>
                {data.byCollector.length === 0 ? (
                  <Empty style={{ padding: 24 }} description="No data" />
                ) : (
                  <Table
                    rowKey={(r) => String(r.userId)}
                    size="small"
                    pagination={false}
                    dataSource={data.byCollector}
                    columns={[
                      { title: 'Collector', dataIndex: 'collector' },
                      { title: 'Receipts', dataIndex: 'receiptCount', width: 100, align: 'right' },
                      { title: 'Amount', dataIndex: 'amount', width: 150, align: 'right', render: (a: string) => <Typography.Text strong>₨ {PKR(a)}</Typography.Text> },
                    ]}
                  />
                )}
              </Card>
            </Col>
          </Row>

          <Card title="Daily collections" styles={{ body: { padding: 0 } }}>
            {data.byDate.length === 0 ? (
              <Empty style={{ padding: 24 }} description="No daily activity" />
            ) : (
              <Table
                rowKey="date"
                size="small"
                pagination={false}
                loading={isFetching}
                dataSource={data.byDate}
                columns={[
                  { title: 'Date', dataIndex: 'date', width: 220, render: (d: string) => dayjs(d).format('dddd, D MMM YYYY') },
                  { title: 'Receipts', dataIndex: 'receiptCount', width: 120, align: 'right', render: (n: number) => <Tag>{n}</Tag> },
                  { title: 'Amount', dataIndex: 'amount', width: 160, align: 'right', render: (a: string) => `₨ ${PKR(a)}` },
                ]}
              />
            )}
          </Card>
        </>
      ) : (
        <Empty />
      )}
    </div>
  );
}
